import { UrlParamKey } from 'app/core/api/api-core/api-constants';
import { UserRole } from 'app/core/api/user/api-user.types';
import { PageNotFoundError } from 'app/core/errors/error-variants';
import IconLib from 'app/shared/components/icon/icon-lib';
import AppRoute from 'app/shared/frames/AppRoute';
import { NavActions } from 'app/shared/page-parts/BottomNav';
import CommonAppRoutes from 'app/shared/page-parts/CommonAppRoutes';
import ChatList from 'app/shared/pages/Chat/ChatList';
import Tasks from 'app/shared/pages/Chat/Tasks';
import ConfirmPassword from 'app/shared/pages/ConfirmPassword';
import PrivacyPolicy from 'app/shared/pages/PrivacyPolicy';
import ResetPassword from 'app/shared/pages/ResetPassword';
import { SlideInfo } from 'app/shared/pages/Welcome';
import SharedRoutePath from 'app/shared/SharedRoutePath';
import useSetupApp from 'app/tools/hooks/use-setup-app';
import imageSlide1 from 'assets/welcome-images/slide1.png';
import imageSlide2 from 'assets/welcome-images/slide2.png';
import imageSlide3 from 'assets/welcome-images/slide3.png';
import React, { useEffect } from 'react';
import { Redirect, Switch } from 'react-router-dom';
import { useNotificationCount } from 'app/core/api/company/api-company';
import { useDispatch } from 'react-redux';
import { contextActions } from 'app/core/redux/context-redux';
import BusinessRoutePath from './BusinessRoutePath';
import RepairerTAndCs from './public/RepairerTAndCs';
import CreateBusinessProfile from './views/create-business-profile/CreateBusinessProfile';
import CreateCompanyAddress from './views/business-profile/CreateCompanyAddress';
import Home from './views/Home';
import NewUserIntro from './views/NewUserIntro';
import UpdateBusinessProfile from './views/business-profile/UpdateBusinessProfile';
import UpdateRepairCapabilities from './views/business-profile/UpdateRepairCapabilities';
import UpdateRepairLocations from './views/business-profile/UpdateRepairLocations';
import BusinessProfile from './views/business-profile/BusinessProfile';
import UpdateBankDetails from './views/business-profile/UpdateBankDetails';
import SelectBusinessType from './views/business-profile/SelectBusinessType';
import UpdatePersonalDetail from './views/business-profile/UpdatePersonalDetail';
import CreateIndividualProfile from './views/create-business-profile/CreateIndividualProfile';
import CreatePersonalAddress from './views/business-profile/CreatePersonalAddress';
import ProductDetails from './views/business-profile/ProductDetail';
import BusinessCalendar from './views/business-profile/BusinessCalendar';
import Notifications from './views/notifications/Notifications';
import BusinessCalendarEventDetail from './views/business-profile/BusinessCalendarEventDetail';
import CreateNewPassword from './views/business-profile/CreateNewPassword';
import Payment from './views/business-profile/payment/Payment';
import History from './views/business-profile/payment/History';
import BankDetails from './views/business-profile/payment/BankDetails';

const navActions: NavActions = [
  {
    label: 'Home',
    value: SharedRoutePath.HOME,
    icon: IconLib.HOME,
  },
  {
    label: 'Repairs',
    value: `${SharedRoutePath.CHAT_LIST}/all`,
    icon: IconLib.CHAT,
  },
  {
    label: 'Calendar',
    value: BusinessRoutePath.REPAIR_CALENDAR,
    icon: IconLib.CALENDAR,
  },
  {
    label: 'Profile',
    value: BusinessRoutePath.PROFILE,
    icon: IconLib.BRIEFCASE,
  },
];

const welcomeSlides: SlideInfo[] = [
  {
    title: 'Welcome to iWarranty',
    subtitle: ['Good for your business,', 'great for our planet.'],
    imgSrc: imageSlide1,
  },
  {
    title: 'Grow your business',
    subtitle: ['Access to new jobs', 'without lifting a finger.'],
    imgSrc: imageSlide2,
  },
  {
    title: 'Skip the paperwork',
    subtitle: [
      'All your job information in the palm',
      'of your hand.',
    ],
    imgSrc: imageSlide3,
  },
];

export default function BusinessApp(): JSX.Element {
  const { resource: notificationCount } = useNotificationCount({
    fresh: true,
  });
  const dispatch = useDispatch();

  useEffect(() => {
    const navs: NavActions = [
      {
        label: 'Home',
        value: SharedRoutePath.HOME,
        icon: IconLib.HOME,
      },
      {
        label: 'Repairs',
        value: `${SharedRoutePath.CHAT_LIST}/all`,
        icon: IconLib.CHAT,
        data: notificationCount?.count,
      },
      {
        label: 'Calendar',
        value: BusinessRoutePath.REPAIR_CALENDAR,
        icon: IconLib.CALENDAR,
      },
      {
        label: 'Profile',
        value: BusinessRoutePath.PROFILE,
        icon: IconLib.BRIEFCASE,
      },
      // {
      //   label: 'Notifications',
      //   value: BusinessRoutePath.NOTIFICATIONS,
      //   icon: IconLib.BELL,
      //   data: notificationCount?.count,
      // },
    ];
    dispatch(contextActions.navActions.set(navs));
  }, [dispatch, notificationCount]);
  const { showWelcome } = useSetupApp(navActions);
  if (showWelcome) return <Redirect to={SharedRoutePath.WELCOME} />;

  return (
    <Switch>
      {/* ------- PUBLIC ROUTES ------- */}
      {CommonAppRoutes(welcomeSlides)}
      <AppRoute
        publicRoute
        path={SharedRoutePath.RESET_PASSWORD}
        display={<ResetPassword />}
        fullScreen
        soloContent
      />
      <AppRoute
        publicRoute
        path={SharedRoutePath.CONFIRM_PASSWORD}
        display={<ConfirmPassword />}
        fullScreen
        soloContent
      />
      <AppRoute
        title="Data & Privacy Policy"
        publicRoute
        path={SharedRoutePath.PRIVACY_POLICY}
        display={<PrivacyPolicy />}
      />
      <AppRoute
        title="Terms and Conditions"
        publicRoute
        path={SharedRoutePath.TERMS_Of_USE}
        display={<RepairerTAndCs />}
      />
      <AppRoute
        publicRoute
        path={SharedRoutePath.CONFIRM_PASSWORD}
        display={<ConfirmPassword />}
        fullScreen
        soloContent
      />
      {/* ------- PRIVATE ROUTES ------- */}
      <AppRoute
        title="Welcome"
        path={BusinessRoutePath.NEW_USER_INTRO}
        display={<NewUserIntro />}
        fullScreen="showHeader"
        noSideNavItems
      />
      <AppRoute
        title="Home"
        path={SharedRoutePath.HOME}
        display={<Home />}
        cardBgColor
        exact
      />
      <AppRoute
        title="Create Profile"
        path={BusinessRoutePath.CREATE_PROFILE}
        display={<CreateBusinessProfile />}
        exact
        noSideNavItems
        fullScreen="showHeader"
      />
      <AppRoute
        title="Create Profile"
        path={BusinessRoutePath.CREATE_PERSONAL_PROFILE}
        display={<CreateIndividualProfile />}
        exact
        noSideNavItems
        fullScreen="showHeader"
      />
      <AppRoute
        title="Business Profile"
        path={BusinessRoutePath.PROFILE}
        display={<BusinessProfile />}
      />
      <AppRoute
        title="Business Profile"
        path={BusinessRoutePath.UPDATE_BUSINESS_PROFILE}
        display={<UpdateBusinessProfile />}
        backButton
      />
      <AppRoute
        title="Business Address"
        path={BusinessRoutePath.UPDATE_BUSINESS_INFORMATION}
        display={<CreateCompanyAddress />}
        backButton
      />
      <AppRoute
        title="Address"
        path={BusinessRoutePath.REPAIR_PERSONAL_ADDRESS}
        display={<CreatePersonalAddress />}
        backButton
      />
      <AppRoute
        title="Repair Categories"
        path={BusinessRoutePath.UPDATE_REPAIR_CATEGORIES}
        display={<UpdateRepairCapabilities />}
        backButton
      />
      <AppRoute
        title="Repair Locations"
        path={BusinessRoutePath.UPDATE_REPAIR_LOCATIONS}
        display={<UpdateRepairLocations />}
        backButton
      />
      {/* <AppRoute
        path={`${BusinessRoutePath.CREATE_PROFILE_PAGE}/:${UrlParamKey.PAGE_NUM}`}
        display={<CreateBusinessProfile />}
        noSideNavItems
        fullScreen="showHeader"
      />
      <AppRoute
        path={`${BusinessRoutePath.CREATE_PERSONAL_PROFILE_PAGE}/:${UrlParamKey.PAGE_NUM}`}
        display={<CreateIndividualProfile />}
        noSideNavItems
        fullScreen="showHeader"
      /> */}
      <AppRoute
        path={`${BusinessRoutePath.CREATE_PROFILE_PAGE}/:${UrlParamKey.PAGE_NUM}`}
        display={<CreateBusinessProfile />}
        backButton
        fullScreen="showHeader"
      />
      <AppRoute
        path={`${BusinessRoutePath.CREATE_PERSONAL_PROFILE_PAGE}/:${UrlParamKey.PAGE_NUM}`}
        display={<CreateIndividualProfile />}
        backButton
        fullScreen="showHeader"
      />
      <AppRoute
        title="Repairs"
        path={`${SharedRoutePath.CHAT_LIST}/:${UrlParamKey.ID}`}
        display={<ChatList userRole={UserRole.REPAIRER} />}
      />
      <AppRoute
        title="Repairs"
        path={SharedRoutePath.CHAT_LIST}
        display={<ChatList userRole={UserRole.REPAIRER} />}
      />
      <AppRoute
        title="Notifications"
        path={BusinessRoutePath.NOTIFICATIONS}
        display={<Notifications />}
      />
      <AppRoute
        title="Calendar"
        path={BusinessRoutePath.REPAIR_CALENDAR}
        display={<BusinessCalendar />}
      />
      <AppRoute
        title="Calendar Detail"
        path={`${BusinessRoutePath.REPAIR_CALENDAR_DETAIL}/:${UrlParamKey.ID}`}
        display={<BusinessCalendarEventDetail />}
        backButton
      />
      <AppRoute
        path={`${SharedRoutePath.CHAT}/:${UrlParamKey.ID}`}
        display={<Tasks userRole={UserRole.REPAIRER} />}
        backButton
        fullScreen="showHeader"
        edgeToEdge
      />
      <AppRoute
        path={`${BusinessRoutePath.UPDATE_BUSINESS_BANK_DETAILS}`}
        display={<UpdateBankDetails />}
        backButton
        fullScreen="showHeader"
        edgeToEdge
      />
      <AppRoute
        path={`${BusinessRoutePath.REPAIR_PERSONAL_DETAIL}`}
        display={<UpdatePersonalDetail />}
        backButton
        fullScreen="showHeader"
        edgeToEdge
      />
      <AppRoute
        path={`${BusinessRoutePath.SELECT_BUSINESS_TYPE}`}
        display={<SelectBusinessType />}
        backButton
        fullScreen="showHeader"
        edgeToEdge
      />
      <AppRoute
        path={`${BusinessRoutePath.REPAIR_VIEW_PRODUCT}/:${UrlParamKey.ID}`}
        display={<ProductDetails />}
        backButton
        edgeToEdge
      />
      <AppRoute
        path={`${BusinessRoutePath.REPAIR_CALENDAR}`}
        display={<BusinessCalendar />}
        backButton
        fullScreen="showHeader"
        edgeToEdge
      />

      <AppRoute
        title="Change password"
        path={SharedRoutePath.CHANGE_PASSWORD}
        display={<CreateNewPassword />}
        backButton
        soloContent
      />
      <AppRoute
        title="Payments"
        path={BusinessRoutePath.PAYMENT}
        display={<Payment />}
        backButton
      />
      <AppRoute
        title="History"
        path={BusinessRoutePath.HISTORY}
        display={<History />}
        backButton
      />
      <AppRoute
        title="Bank Details"
        path={BusinessRoutePath.BANK_DETAILS}
        display={<BankDetails />}
        backButton
      />
      {/* -------- NOT-FOUND ROUTE ------ */}
      <AppRoute
        path="*"
        display={() => {
          throw new PageNotFoundError();
        }}
        publicRoute
      />
    </Switch>
  );
}
